import React, { useRef, useState } from 'react';
import { NodeStatus } from 'components';
import styles from './GroupSettingsDeviceList.module.css';
import { IoVolumeHigh, IoVolumeMute } from 'react-icons/io5';
import Hls from 'hls.js';
import WidgetLoadingAnimation from 'components/Widgets/WidgetLoadingAnimation';

/**
 * @param {{devices: object[], onSelect: (deviceId: number)}}
 */
export default function GroupSettingsDeviceList({
  devices,
  highlightIds = [],
  onSelect = () => {},
}) {
  const audioRef = useRef(null);
  const hlsRef = useRef(null);

  const [playing, setPlaying] = useState(null);
  const [errMsg, setErrorMsg] = useState({});

  const onListen = (evt, deviceId) => {
    evt.stopPropagation();
    setErrorMsg({});

    if (hlsRef.current) hlsRef.current.destroy();

    const url = `${process.env.PUBLIC_API_URL}/audio/${deviceId}/hls/index.m3u8`;

    const hls = new Hls();
    hlsRef.current = hls;
    hls.loadSource(url);
    hls.attachMedia(audioRef.current);
    hls.on(Hls.Events.ERROR, (_event, data) => {
      console.error('Error in DeviceControls:Hls:', data);
      setErrorMsg({ [deviceId]: data.type });
    });

    audioRef.current?.addEventListener('playing', clearErrorMsg);
    audioRef.current?.addEventListener('waiting', buffering);
    audioRef.current?.addEventListener('stalled', buffering);

    setPlaying(deviceId);
  };

  const onStopListen = (evt) => {
    evt.stopPropagation();
    setErrorMsg({});

    hlsRef.current?.destroy();
    hlsRef.current = null;

    audioRef.current?.pause();
    audioRef.current?.removeAttribute('src');
    audioRef.current?.load();
    audioRef.current?.removeEventListener('playing', clearErrorMsg);
    audioRef.current?.removeEventListener('waiting', buffering);
    audioRef.current?.removeEventListener('stalled', buffering);

    setPlaying(null);
  };

  const buffering = () => setErrorMsg('Loading');

  const clearErrorMsg = () => setErrorMsg('');

  return (
    <div className="d-flex flex-column flex-fill rounded-3 bg-primary p-2 overflow-auto">
      <div className="fw-semibold px-2 pb-2">Devices in Group</div>
      <div className="row g-0 px-2 pb-1 border-bottom border-light-subtle fw-semibold">
        <div className="col-6 text-truncate">Device Name</div>
        <div className="col-2 text-center">Node ID</div>
        <div className="col-1" />
        <div className="col-3 text-end">Status</div>
      </div>

      {devices.map((device) => (
        <div
          key={device.id ?? device.node_id ?? device.alias}
          className="row g-0 align-items-center px-2 py-2 border-bottom border-light-subtle"
          onClick={() => onSelect(device.id)}
        >
          <div className="col-6 text-truncate">{device.alias}</div>
          <div className="col-2 text-center">{device.node_id}</div>
          <div className="col-1 d-flex justify-content-center">
            {device?.camera && playing !== device.id && (
              <button className={styles['sound-button']} onClick={(e) => onListen(e, device.id)}>
                <IoVolumeMute size={20} />
              </button>
            )}
            {device?.camera && playing === device.id && (
              <button className={styles['sound-button']} onClick={onStopListen}>
                <IoVolumeHigh size={20} />
              </button>
            )}
          </div>
          <div className="col-3 d-flex justify-content-end">
            <NodeStatus device={device} />
          </div>

          {errMsg?.[device.id] && <div className={styles.error}>{errMsg[device.id]}</div>}
        </div>
      ))}

      <audio ref={audioRef} className={styles.audio} autoPlay />
    </div>
  );
}
